import React, { useState, useEffect, useCallback } from 'react'
import { useHistory, Link } from 'react-router-dom'
import axios from 'axios'
import { get } from 'lodash'
import { PlusIcon } from '@heroicons/react/outline'

import ApplicationLayout from '../leevo_ui/ApplicationLayout/ApplicationLayout'
import Notification from '../leevo_ui/Notification/Notification'
import useFacility from '../../utilities/hooks/useFacility'
import useAuth from '../../utilities/hooks/useAuth'
import { requestCreator } from '../../utilities/requests'
import DisclosuresTable from './DisclosuresTable/DisclosuresTable'
import ResponsesTable from './ResponsesTable/ResponsesTable'
import EditDisclosureModal from './EditDisclosureModal/EditDisclosureModal'

const { get: getDisclosures, cancel: cancelDisclosuresRequests } = requestCreator()

function FacilitiesDisclosuresManage() {
  const { push: navigate, replace: redirect } = useHistory()
  const { facility, isLoading: isFacilityLoading } = useFacility()
  const [disclosures, setDisclosures] = useState([])
  const [responses, setResponses] = useState([])
  const [isLoadingData, setIsLoadingData] = useState(true)
  const [editingDisclosure, setEditingDisclosure] = useState(null)
  const [isSaving, setIsSaving] = useState(false)
  const [notification, setNotification] = useState(null)
  const { isSignedIn } = useAuth()

  const loadData = useCallback(async () => {
    try {
      const data = await getDisclosures(`/api/facilities/${facility.id}/disclosures`, {
        params: {
          fields: {
            disclosureResponse: 'agreed,updated_at',
            student: 'fullName',
            user: 'fullName'
          },
          include: 'responses,responses.student,responses.respondent'
        }
      })

      const disclosuresWithCount = data.map((disclosure) => ({
        ...disclosure,
        responseCount: (disclosure.responses && disclosure.responses.length) || 0
      }))

      setDisclosures(disclosuresWithCount)

      const allResponses = data.flatMap((disclosure) =>
        (disclosure.responses || []).map((response) => ({
          id: response.id,
          disclosureId: disclosure.id,
          disclosureName: disclosure.name,
          studentName: response.student && response.student.fullName,
          agreed: response.agreed,
          date: response.updatedAt,
          respondentName: response.respondent && response.respondent.fullName
        }))
      )
      setResponses(allResponses)
      setIsLoadingData(false)
    } catch (error) {
      if (!axios.isCancel(error)) {
        if (get(error, 'response.status') === 401 && !isSignedIn) {
          redirect('/login')
        } else {
          redirect('/500')
        }
      }
    }
  }, [facility.id, isSignedIn, redirect])

  useEffect(() => {
    if (!isFacilityLoading) {
      loadData()
    }

    return cancelDisclosuresRequests
  }, [isFacilityLoading, loadData])

  async function handleSaveDisclosure(disclosureId, formData) {
    setIsSaving(true)
    try {
      await axios.put(`/api/facilities/${facility.id}/disclosures/${disclosureId}`, {
        disclosure: {
          name: formData.name,
          disclosure_type: formData.disclosureType,
          agreement: formData.agreement
        }
      })

      setDisclosures((prev) => prev.map((d) => (d.id === disclosureId ? { ...d, ...formData } : d)))

      setEditingDisclosure(null)
      setNotification({
        message: 'Disclosure updated successfully!',
        color: 'green'
      })
      setTimeout(() => setNotification(null), 5000)
    } catch (error) {
      const errorMessage =
        (error.response && error.response.data && error.response.data.error) || 'Failed to update disclosure'
      setNotification({
        message: errorMessage,
        color: 'red'
      })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <ApplicationLayout>
      <div className="min-h-screen bg-gray-100 pb-10">
        <div className="px-4 py-6 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 mb-6 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Disclosures</h1>
              <p className="mt-1 text-sm text-gray-600 sm:mt-2">Manage disclosure agreements and view responses</p>
            </div>
            <button
              onClick={() => navigate(`/facilities/${facility.id}/disclosures/new`)}
              className="inline-flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md sm:w-auto hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Add New
            </button>
          </div>

          {notification && (
            <Notification show color={notification.color} className="mb-6">
              {notification.message}
            </Notification>
          )}

          <Notification
            show
            data-cy="disclosures-notice"
            color="gray"
            className="mb-6 text-center mx-auto bg-white max-w-full !w-full text-balance"
          >
            By using Leevo&apos;s disclosure features, you are agreeing to our disclosure liability notice.{' '}
            <Link to="/terms" className="text-purple-600 hover:text-purple-400">
              See full terms
            </Link>
          </Notification>

          <div className="mb-8 overflow-hidden bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Disclosure Management</h2>
              <p className="mt-1 text-sm text-gray-600">
                Toggle active status and required settings. Changes save automatically.
              </p>
            </div>
            {isLoadingData ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-12 h-12 border-4 border-purple-600 rounded-full border-t-transparent animate-spin"></div>
              </div>
            ) : (
              <DisclosuresTable
                disclosures={disclosures}
                onDisclosuresChange={setDisclosures}
                onEditDisclosure={setEditingDisclosure}
                facilityId={facility.id}
              />
            )}
          </div>

          <div className="overflow-hidden bg-white rounded-lg shadow">
            <div className="px-6 py-4">
              {isLoadingData ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-12 h-12 border-4 border-purple-600 rounded-full border-t-transparent animate-spin"></div>
                </div>
              ) : (
                <ResponsesTable responses={responses} disclosures={disclosures} />
              )}
            </div>
          </div>
        </div>
      </div>

      <EditDisclosureModal
        isOpen={!!editingDisclosure}
        onClose={() => setEditingDisclosure(null)}
        disclosure={editingDisclosure}
        onSave={handleSaveDisclosure}
        isLoading={isSaving}
      />
    </ApplicationLayout>
  )
}

export default FacilitiesDisclosuresManage
